import { FC, useMemo, useState } from 'react';

import {
  ChakraBox,
  ChakraHStack,
  ChakraVStack,
  FAIconName,
  FireflyButton,
  FormLabel,
  ModalV2,
  MultiSelectOption,
  RadioV2,
  useToastMessage,
} from '@equip.health/ui';
import {
  CANCELLATION_SCOPE_LABEL,
  RecurringAppointmentUpdateOption,
} from '~/lib/constants/calendar.constants';
import { useCancelAppointmentMutation } from '~/store/services/appointment';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import {
  AppointmentCancellationEvents,
  AppointmentCancellationSource,
} from '~/analytics/patientEvents';
import { AnalyticsEvent } from '~/analytics';
import AppointmentCancelReason, {
  CancellationReasonOption,
} from './AppointmentCancelReason';

const TOP_LEVEL_ID = 'cancel-appointment-modal-v2';

interface CancelAppointmentModalProps {
  appointmentExternalId: string;
  isOpen: boolean;
  isRecurring: boolean;
  onClose: () => void;
  appointmentTitle?: string;
}
const CancelAppointmentModal: FC<CancelAppointmentModalProps> = ({
  isRecurring,
  isOpen,
  onClose,
  appointmentExternalId,
  appointmentTitle,
}) => {
  const [
    selectedRecurringAppointmentUpdateOption,
    setSelectedRecurringAppointmentUpdateOption,
  ] = useState<RecurringAppointmentUpdateOption>(
    RecurringAppointmentUpdateOption.ONLY,
  );
  const [selectedReason, setSelectedReason] =
    useState<CancellationReasonOption>();
  const { createToast } = useToastMessage();

  const [selectedSubReason, setSelectedSubReason] =
    useState<MultiSelectOption>();

  const [isCancelError, setIsCancelError] = useState<boolean>(false);
  const [deleteAppointment, { isLoading: isCancelAppointmentLoading }] =
    useCancelAppointmentMutation();
  const { trackEvent } = useAnalytics();

  const handleOnClose = () => {
    setSelectedRecurringAppointmentUpdateOption(
      RecurringAppointmentUpdateOption.ONLY,
    );
    setSelectedReason(undefined);
    setIsCancelError(false);
    onClose();
  };

  const shouldDisableCancelButton = useMemo(() => {
    if (selectedReason?.subReasons?.length > 0) return !selectedSubReason;

    return (
      !selectedReason ||
      (isRecurring && selectedRecurringAppointmentUpdateOption === undefined)
    );
  }, [
    selectedReason,
    selectedSubReason,
    isRecurring,
    selectedRecurringAppointmentUpdateOption,
  ]);

  const cancelAppointment = () => {
    deleteAppointment({
      appointmentExternalId,
      cancellationReason: selectedReason?.value,
      cancellationSubReason: selectedSubReason?.value,
      cancelType: selectedRecurringAppointmentUpdateOption,
    })
      .unwrap()
      .then(() => {
        trackEvent(AppointmentCancellationEvents.cancelAppointmentCTAClicked, {
          appointmentId: appointmentExternalId,
          appointmentTitle,
          cancellationReason: selectedReason?.value,
          isCancelingSeries:
            selectedRecurringAppointmentUpdateOption ===
            RecurringAppointmentUpdateOption.ALL,
          isRecurringEvent: isRecurring,
          patientCancellationReason: selectedSubReason?.value,
        });
        createToast({
          icon: FAIconName.checkRegular,
          id: 'cancel-appointment-success_toast',
          text: 'Appointment cancelled',
          variant: 'success',
        });
        handleOnClose();
      })
      .catch(() => {
        setIsCancelError(true);
      });
  };

  const trackXClickedFromCancellationScreen = () => {
    trackEvent(AppointmentCancellationEvents.xClicked as AnalyticsEvent, {
      source: isRecurring
        ? AppointmentCancellationSource.recurringAppointmentCancellationScreen
        : AppointmentCancellationSource.singleAppointmentCancellationScreen,
    });
  };

  return (
    <ModalV2
      id={TOP_LEVEL_ID}
      isOpen={isOpen}
      onClose={() => {
        trackXClickedFromCancellationScreen();
        handleOnClose();
      }}
      size="md"
      title="Cancel appointment"
    >
      {isRecurring && (
        <>
          <ChakraBox marginTop="4px">
            <FormLabel
              id={`${TOP_LEVEL_ID}__cancel-scope-label`}
              label={CANCELLATION_SCOPE_LABEL}
              shouldShowAsteriskWhenRequired
            />
          </ChakraBox>
          <ChakraVStack alignItems="start" gap={3}>
            <ChakraVStack alignItems="start" gap="18px" marginTop="12px">
              {Object.values(RecurringAppointmentUpdateOption).map((option) => (
                <RadioV2
                  id={`${TOP_LEVEL_ID}__${option}`}
                  isChecked={
                    selectedRecurringAppointmentUpdateOption === option
                  }
                  key={option}
                  label={option}
                  onChange={() =>
                    setSelectedRecurringAppointmentUpdateOption(option)
                  }
                  size="md"
                />
              ))}
            </ChakraVStack>
          </ChakraVStack>
        </>
      )}
      <ChakraVStack alignItems="start" mt={isRecurring ? '24px' : '4px'}>
        <AppointmentCancelReason
          id={TOP_LEVEL_ID}
          selectedReason={selectedReason}
          setSelectedReason={setSelectedReason}
          selectedSubReason={selectedSubReason}
          setSelectedSubReason={setSelectedSubReason}
          isCancelError={isCancelError}
        />
        <ChakraHStack
          justifyContent="space-between"
          marginTop="18px"
          width="100%"
        >
          <FireflyButton
            id={`${TOP_LEVEL_ID}__keep-appointment`}
            onClick={() => {
              trackXClickedFromCancellationScreen();
              handleOnClose();
            }}
            variant="secondary"
          >
            Keep appointment
          </FireflyButton>
          <FireflyButton
            id={`${TOP_LEVEL_ID}__cancel-appointment`}
            isDisabled={shouldDisableCancelButton}
            isLoading={isCancelAppointmentLoading}
            leftIcon={FAIconName.trashRegular}
            onClick={cancelAppointment}
            variant="accent.critical"
          >
            Cancel appointment
          </FireflyButton>
        </ChakraHStack>
      </ChakraVStack>
    </ModalV2>
  );
};

export default CancelAppointmentModal;
